import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { 
  Smartphone, 
  Settings2, 
  Cloud, 
  BarChart3, 
  Shield,
  ChevronRight,
  Check
} from 'lucide-react';

const services = [
  {
    icon: Smartphone,
    title: 'Mobil Uygulama Geliştirme',
    description: 'iOS ve Android platformları için yüksek performanslı, kullanıcı dostu mobil uygulamalar geliştiriyoruz.',
    color: '#00E5FF',
    features: [
      'Native iOS & Android geliştirme',
      'Cross-platform çözümler (React Native, Flutter)',
      'UI/UX tasarım',
      'App Store & Play Store yayınlama',
      'Bakım ve güncelleme desteği'
    ]
  },
  {
    icon: Settings2,
    title: 'Kurumsal Otomasyon Sistemleri',
    description: 'İş süreçlerinizi optimize eden, verimliliği artıran özel otomasyon çözümleri sunuyoruz.',
    color: '#2962FF',
    features: [
      'İş akışı otomasyonu',
      'ERP & CRM entegrasyonları',
      'Özel yazılım geliştirme',
      'API entegrasyonları',
      'Raporlama sistemleri'
    ]
  },
  {
    icon: Cloud,
    title: 'Bulut Çözümleri ve Entegrasyon',
    description: 'AWS, Azure ve Google Cloud üzerinde ölçeklenebilir bulut altyapıları kuruyoruz.',
    color: '#B026FF',
    features: [
      'Cloud mimari tasarımı',
      'Microservices geliştirme',
      'DevOps & CI/CD pipeline',
      'Container orchestration',
      'Serverless çözümler'
    ]
  },
  {
    icon: BarChart3,
    title: 'SEO & Dijital Pazarlama',
    description: 'Markanızı dijital dünyada görünür kılan, hedef kitlenize ulaşmanızı ve satışlarınızı artırmanızı sağlayan stratejik çözümler.',
    color: '#00E5FF',
    features: [
      'Teknik SEO & site içi optimizasyon',
      'Anahtar kelime ve rakip analizi',
      'Google Ads & Meta reklam yönetimi',
      'Dönüşüm optimizasyonu (CRO)',
      'SEO uyumlu içerik stratejisi'
    ]
  },
  {
    icon: Shield,
    title: 'Siber Güvenlik Çözümleri',
    description: 'Sistemlerinizi ve verilerinizi korumak için kapsamlı güvenlik hizmetleri sağlıyoruz.',
    color: '#2962FF',
    features: [
      'Güvenlik denetimi',
      'Penetrasyon testleri',
      'Güvenlik altyapısı kurulumu',
      'Veri koruma çözümleri',
      '24/7 güvenlik izleme'
    ]
  },
];

// 3D Tilt Card Component
const ServiceCard = ({ service, index }) => {
  const cardRef = useRef(null);
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);
  const [glowPosition, setGlowPosition] = useState({ x: 50, y: 50 });

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const rotateXValue = ((y - centerY) / centerY) * -8;
    const rotateYValue = ((x - centerX) / centerX) * 8;
    
    setRotateX(rotateXValue);
    setRotateY(rotateYValue);
    setGlowPosition({
      x: (x / rect.width) * 100,
      y: (y / rect.height) * 100,
    });
  };

  const handleMouseLeave = () => {
    setRotateX(0);
    setRotateY(0);
    setGlowPosition({ x: 50, y: 50 });
  };

  const Icon = service.icon;

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="group relative cursor-pointer"
      style={{
        transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
        transformStyle: 'preserve-3d',
        transition: 'transform 0.1s ease-out',
      }}
      data-testid={`service-card-${index}`}
    >
      {/* Glow effect */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg pointer-events-none"
        style={{
          background: `radial-gradient(circle at ${glowPosition.x}% ${glowPosition.y}%, ${service.color}20, transparent 50%)`,
        }}
      />

      {/* Card */}
      <div className="relative p-8 rounded-lg glass group-hover:border-opacity-30 transition-all duration-300 h-full"
           style={{ borderColor: service.color }}>
        {/* Icon */}
        <div 
          className="w-16 h-16 mb-6 flex items-center justify-center rounded-lg"
          style={{
            background: `${service.color}15`,
            boxShadow: `0 0 30px ${service.color}30`,
          }}
        >
          <Icon 
            size={32} 
            style={{ color: service.color }}
            className="group-hover:scale-110 transition-transform duration-300"
          />
        </div>

        {/* Title */}
        <h3 className="font-display text-xl font-bold text-white mb-4 group-hover:text-brand-cyan transition-colors">
          {service.title}
        </h3>

        {/* Description */}
        <p className="font-mono text-sm text-text-secondary leading-relaxed mb-6">
          {service.description}
        </p>

        {/* Features */}
        <ul className="space-y-2">
          {service.features.map((feature, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-text-muted">
              <Check size={14} className="text-brand-cyan mt-1 flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        {/* Decorative corner */}
        <div 
          className="absolute top-0 right-0 w-12 h-12 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{
            background: `linear-gradient(135deg, transparent 50%, ${service.color}30 50%)`,
          }}
        />

        {/* Bottom glow line */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{
            background: `linear-gradient(90deg, transparent, ${service.color}, transparent)`,
          }}
        />
      </div>
    </motion.div>
  );
};

function ServicesPage() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <div className="pt-24" data-testid="services-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-background-primary relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 -left-32 w-64 h-64 bg-brand-cyan/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 -right-32 w-64 h-64 bg-brand-purple/5 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-brand-cyan font-mono mb-4">
              Hizmetlerimiz
            </p>
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Yazılım <span className="gradient-text">Çözümlerimiz</span>
            </h1>
            <p className="font-mono text-text-secondary max-w-2xl mx-auto">
              İşletmenizin dijital dönüşümünü hızlandırmak için kapsamlı yazılım hizmetleri sunuyoruz.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section ref={sectionRef} className="py-16 bg-background-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 3).map((service, index) => (
              <ServiceCard key={index} service={service} index={index} />
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8 max-w-4xl mx-auto">
            {services.slice(3).map((service, index) => (
              <ServiceCard key={index + 3} service={service} index={index + 3} />
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-background-primary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-brand-cyan font-mono mb-4">
              Çalışma Sürecimiz
            </p>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white">
              Nasıl <span className="gradient-text">Çalışıyoruz?</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Analiz', desc: 'İhtiyaçlarınızı dinliyor ve analiz ediyoruz' },
              { step: '2', title: 'Planlama', desc: 'Projeniz için detaylı yol haritası çiziyoruz' },
              { step: '3', title: 'Geliştirme', desc: 'En son teknolojilerle geliştirme yapıyoruz' },
              { step: '4', title: 'Teslimat', desc: 'Projenizi test edip teslim ediyoruz' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl font-display font-bold text-brand-cyan/30 mb-4">
                  {item.step}
                </div>
                <h3 className="font-display text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="font-mono text-sm text-text-secondary">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-secondary">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display text-2xl md:text-3xl font-bold text-white mb-6"
          >
            Projeniz İçin <span className="gradient-text">Teklif Alın</span>
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <Link
              to="/iletisim"
              className="inline-flex items-center gap-2 px-8 py-4 bg-brand-cyan text-background-primary font-display font-bold text-sm uppercase tracking-wider
                         hover:bg-white hover:shadow-[0_0_30px_rgba(0,229,255,0.6)] transition-all duration-300"
              data-testid="services-cta"
            >
              İletişime Geç <ChevronRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default ServicesPage;
